// app/src/screens/VerifyEmail.jsx — THE hard email-verification gate (mirrors Quocia-FB's
// verify/page.tsx). A signed-in but unverified email/password account lands HERE and cannot
// reach the app until it verifies. The page polls the Firebase user every few seconds, so the
// instant the user clicks the emailed link (in any tab or on their phone) it detects the flip,
// pulls the verified claim through /v1/me (which stamps email_verified_at + fires the free
// signup grant), and the parent gate (App.SignedInShell) swaps this page for the studio.
//
// OAuth (Google/LinkedIn) users arrive provider-verified, so the gate never shows them this.

import React, { useEffect, useRef, useState } from 'react';
import { navigate } from '@common/router.js';
import { Mail } from 'lucide-react';
import { useI18n } from '../i18n/index.jsx';
import { useSession } from '../lib/session.jsx';
import { AuthFrame } from './SignIn.jsx';

export default function VerifyEmail() {
  const { t } = useI18n();
  const { auth, api, me, refreshMe, refreshCredits } = useSession();
  const [checking, setChecking] = useState(false);
  const [resending, setResending] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [msg, setMsg] = useState(null); // { type: 'ok' | 'err', text }
  const busyRef = useRef(false);        // one verification check at a time (poll ⨯ button race)

  // One check: reload the Firebase user (light); if it flips verified, force a fresh token so
  // the server sees the claim, re-pull /v1/me (stamps + grants), refresh credits. When
  // me.email_verified turns true the parent gate unmounts this page for the app.
  async function checkVerified(manual = false) {
    if (busyRef.current) return;
    busyRef.current = true;
    if (manual) { setChecking(true); setMsg(null); }
    try {
      const verified = await auth.reloadUser();
      if (verified) {
        await auth.refreshToken();          // fresh token carries email_verified=true
        const fresh = await refreshMe();    // server stamps email_verified_at + fires the grant
        if (fresh && fresh.email_verified) {
          await refreshCredits().catch(() => {});
          return; // gate swaps us into the studio — nothing more to do
        }
      }
      if (manual) setMsg({ type: 'err', text: t('verify.notVerified') });
    } catch {
      if (manual) setMsg({ type: 'err', text: t('common.error') });
    } finally {
      busyRef.current = false;
      if (manual) setChecking(false);
    }
  }

  // poll every 3s while the gate is showing
  useEffect(() => {
    const id = setInterval(() => { checkVerified(false); }, 3000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // resend cooldown ticker
  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(id);
  }, [cooldown]);

  async function resend() {
    if (resending || cooldown > 0) return;
    setResending(true); setMsg(null);
    try {
      const r = await api.post('auth/resend-verification', { clientOrigin: window.location.origin });
      const b = r.body || {};
      if (r.kind === 'ok' && b.success) {
        setMsg({ type: 'ok', text: t('verify.sent') });
        setCooldown(60);
      } else if (b.error === 'already_verified') {
        await checkVerified(true); // already done elsewhere — advance
      } else {
        setMsg({ type: 'err', text: t('verify.sendFailed') });
      }
    } catch {
      setMsg({ type: 'err', text: t('verify.sendFailed') });
    } finally {
      setResending(false);
    }
  }

  async function doSignOut() {
    try { await auth.signOut(); } catch { /* clearing the session is best-effort */ }
    navigate('/signin');
  }

  return (
    <AuthFrame title={t('verify.title')} subtitle="">
      <div style={{ textAlign: 'center' }}>
        <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'var(--coral-soft, rgba(255,90,80,.12))', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
          <Mail size={26} color="var(--coral)" strokeWidth={2} />
        </div>
        <p className="muted" style={{ fontSize: 14, margin: '0 0 4px' }}>{t('verify.sentTo')}</p>
        <p style={{ fontWeight: 700, margin: '0 0 16px', wordBreak: 'break-all' }}>{me && me.email}</p>
        <p className="muted" style={{ fontSize: 13, lineHeight: 1.5, margin: '0 0 18px' }}>{t('verify.checkInbox')}</p>
        {msg && <p style={{ fontSize: 13, margin: '0 0 14px', color: msg.type === 'ok' ? 'var(--mint)' : 'var(--coral)' }}>{msg.text}</p>}
        <button className="q-btn press btn-primary" style={{ width: '100%' }} disabled={checking} onClick={() => checkVerified(true)}>
          {checking ? t('verify.checking') : t('verify.verifiedButton')}
        </button>
        <button className="q-btn press btn-ghost" style={{ width: '100%', marginBlockStart: 8 }} disabled={resending || cooldown > 0} onClick={resend}>
          {resending ? t('verify.sending') : cooldown > 0 ? t('verify.resendCooldown', { seconds: cooldown }) : t('verify.resend')}
        </button>
        <button className="q-btn press" style={{ width: '100%', marginBlockStart: 8, background: 'none', border: 'none', color: 'var(--text-faint)', fontSize: 13, cursor: 'pointer' }} onClick={doSignOut}>
          {t('verify.signOut')}
        </button>
        <p className="muted" style={{ fontSize: 12, margin: '14px 0 0', lineHeight: 1.5 }}>{t('verify.help')}</p>
      </div>
    </AuthFrame>
  );
}
